import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Pagination from '../components/Pagination';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import {
  Mail, MailCheck, MailX, RefreshCw, Search, X, Send,
  UserCheck, FileText, CreditCard, Bell, AlertTriangle,
  Inbox, Clock, XCircle, Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_META = {
  welcome:              { label: 'Selamat Datang',    color: 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',     icon: UserCheck },
  first_invoice:        { label: 'Tagihan Pertama',   color: 'bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800', icon: FileText },
  payment_confirmation: { label: 'Konfirmasi Bayar',  color: 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800', icon: CreditCard },
  reminder_h5:          { label: 'Reminder H-5',      color: 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800', icon: Bell },
  overdue_h1:           { label: 'Lewat Jatuh Tempo', color: 'bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800',       icon: AlertTriangle },
  test:                 { label: 'Test Email',        color: 'bg-slate-50 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',    icon: Mail },
  manual_resend:        { label: 'Kirim Ulang',       color: 'bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800', icon: Send },
};

// Left border accent color per template (visual scanning aid)
const ACCENT_BAR = {
  welcome:              'bg-blue-400',
  first_invoice:        'bg-indigo-400',
  payment_confirmation: 'bg-emerald-400',
  reminder_h5:          'bg-amber-400',
  overdue_h1:           'bg-rose-400',
  test:                 'bg-slate-300',
  manual_resend:        'bg-purple-400',
};

// Skeleton row for loading state — creates visual continuity while data loads
const SkeletonRow = () => (
  <tr className="border-b border-slate-100 dark:border-slate-800/50">
    {[...Array(7)].map((_, i) => (
      <td key={i} className="py-4 px-6">
        <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" style={{ width: `${60 + (i * 5) % 40}%` }} />
      </td>
    ))}
  </tr>
);

const StatCard = ({ icon: Icon, label, value, tint, testId }) => (
  <Card
    data-testid={testId}
    className={`p-5 border-0 shadow-sm hover:shadow-md transition-all duration-200 ${tint}`}
  >
    <div className="flex items-center justify-between">
      <div className="space-y-1.5">
        <div className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</div>
        <div className="text-2xl font-bold tabular-nums">{value.toLocaleString('id-ID')}</div>
      </div>
      <div className="w-11 h-11 rounded-xl bg-white/60 dark:bg-black/20 flex items-center justify-center backdrop-blur-sm">
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

const EmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, customers: 0 });
  const [pageSize, setPageSize] = useState(25);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.set('limit', pageSize);
      params.set('offset', (page - 1) * pageSize);
      if (search.trim()) params.set('search', search.trim());
      if (templateFilter) params.set('template_type', templateFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await axios.get(`${API_URL}/api/v2/email-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.data || [];
      setLogs(data);
      setTotal(res.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal memuat riwayat email');
    } finally {
      setLoading(false);
    }
  }, [search, templateFilter, statusFilter, dateFrom, dateTo, page, pageSize]);

  // Separate lightweight stats fetch (no filters) for header cards
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [allRes, sentRes, failRes] = await Promise.all([
        axios.get(`${API_URL}/api/v2/email-logs?limit=1`, { headers }),
        axios.get(`${API_URL}/api/v2/email-logs?limit=1&status=sent`, { headers }),
        axios.get(`${API_URL}/api/v2/email-logs?limit=1&status=failed`, { headers }),
      ]);
      const totalAll = allRes.data?.total || 0;
      const sent = sentRes.data?.total || 0;
      const failed = failRes.data?.total || 0;
      // Approximate unique customers from current page data
      const uniqueCustomers = new Set(logs.map((l) => l.customer_id).filter(Boolean)).size;
      setStats({ total: totalAll, sent, failed, customers: uniqueCustomers });
    } catch {
      /* silent */
    }
  }, [logs]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); }, [logs, fetchStats]);

  const handleResendPayment = async (log) => {
    if (!log.invoice_id) {
      toast.warning('Hanya email konfirmasi pembayaran yang bisa dikirim ulang saat ini');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/v2/invoices/${log.invoice_id}/resend-payment-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        toast.success('Email sedang dikirim ulang');
        setTimeout(() => fetchLogs(), 3500);
      } else {
        toast.error(res.data?.message || 'Gagal kirim ulang');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal kirim ulang');
    }
  };

  const clearFilters = () => {
    setSearch(''); setTemplateFilter(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); setPage(1);
  };

  const hasActiveFilters = useMemo(
    () => !!(search || templateFilter || statusFilter || dateFrom || dateTo),
    [search, templateFilter, statusFilter, dateFrom, dateTo]
  );

  const formatTimeShort = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      const day = format(d, 'dd MMM', { locale: idLocale });
      const time = format(d, 'HH:mm');
      return { day, time };
    } catch {
      return { day: '-', time: '' };
    }
  };

  return (
    <div className="space-y-8 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8 animate-in fade-in duration-300" data-testid="email-logs-page">
      {/* ===== HEADER ===== */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2">
        <div className="flex items-start gap-4">
          <div className="pt-1">
            <Mail className="w-7 h-7 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
              Riwayat Notifikasi Email
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl leading-relaxed">
              Semua email yang pernah terkirim: welcome, tagihan pertama, konfirmasi bayar, reminder H-5, overdue H+1, dan kirim ulang manual
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={fetchLogs}
          disabled={loading}
          data-testid="email-logs-refresh"
          className="gap-2 shadow-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* ===== STATS CARDS ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testId="stat-total"
          icon={Inbox}
          label="Total Notifikasi"
          value={stats.total}
          tint="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 text-slate-700 dark:text-slate-200"
        />
        <StatCard
          testId="stat-sent"
          icon={MailCheck}
          label="Berhasil Terkirim"
          value={stats.sent}
          tint="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/40 text-emerald-700 dark:text-emerald-300"
        />
        <StatCard
          testId="stat-failed"
          icon={XCircle}
          label="Gagal Kirim"
          value={stats.failed}
          tint="bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-950/40 dark:to-red-950/40 text-rose-700 dark:text-rose-300"
        />
        <StatCard
          testId="stat-customers"
          icon={Users}
          label="Pelanggan (halaman ini)"
          value={stats.customers}
          tint="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/40 dark:to-blue-950/40 text-indigo-700 dark:text-indigo-300"
        />
      </div>

      {/* ===== FILTER PANEL ===== */}
      <Card className="p-6 shadow-sm border-slate-200 dark:border-slate-700/60">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div className="md:col-span-2 space-y-2">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wide">
              Cari
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="email-logs-search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Email, nama pelanggan, atau subject..."
                className="pl-10 h-10 text-sm"
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wide">
              Jenis Email
            </label>
            <select
              data-testid="email-logs-template-filter"
              value={templateFilter}
              onChange={(e) => { setTemplateFilter(e.target.value); setPage(1); }}
              className="h-10 w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            >
              <option value="">Semua Jenis</option>
              {Object.entries(TEMPLATE_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wide">
              Status Kirim
            </label>
            <select
              data-testid="email-logs-status-filter"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="h-10 w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            >
              <option value="">Semua Status</option>
              <option value="sent">Terkirim</option>
              <option value="failed">Gagal</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mt-5 pt-5 border-t border-slate-100 dark:border-slate-800">
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wide">
              Dari Tanggal
            </label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="h-10 text-sm"
              data-testid="email-logs-date-from"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wide">
              Sampai Tanggal
            </label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="h-10 text-sm"
              data-testid="email-logs-date-to"
            />
          </div>
          <div className="md:col-span-2 flex items-end justify-end">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="gap-2 text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                data-testid="email-logs-clear-filters"
              >
                <X className="w-4 h-4" />
                Reset Semua Filter
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* ===== RESULTS TABLE ===== */}
      <Card className="overflow-hidden shadow-sm border-slate-200 dark:border-slate-700/60">
        {/* Table header info + page size selector */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 bg-slate-50/50 dark:bg-slate-800/30 border-b border-slate-200 dark:border-slate-700/60">
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <Clock className="w-4 h-4" />
            <span className="font-medium">{total.toLocaleString('id-ID')}</span>
            <span className="text-slate-500">notifikasi ditemukan</span>
            {loading && (
              <span className="ml-3 text-xs text-slate-500 flex items-center gap-1.5">
                <RefreshCw className="w-3 h-3 animate-spin" /> memuat...
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <label htmlFor="page-size" className="text-slate-500 dark:text-slate-400 whitespace-nowrap">Tampilan entri</label>
            <select
              id="page-size"
              data-testid="email-logs-page-size"
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
              className="h-8 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 pr-6 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white dark:bg-slate-800/20 border-b border-slate-200 dark:border-slate-700/60">
              <tr>
                <th className="text-left py-4 px-6 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Waktu</th>
                <th className="text-left py-4 px-6 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Pelanggan</th>
                <th className="text-left py-4 px-6 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Jenis</th>
                <th className="text-left py-4 px-6 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Penerima</th>
                <th className="text-left py-4 px-6 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Invoice</th>
                <th className="text-center py-4 px-6 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
                <th className="text-center py-4 px-6 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && logs.length === 0 && (
                <>
                  <SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow />
                </>
              )}
              {!loading && logs.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-16">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        <Inbox className="w-8 h-8 text-slate-400" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Belum ada notifikasi</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          {hasActiveFilters ? 'Coba ubah filter atau reset semua filter' : 'Notifikasi email akan muncul di sini setelah terkirim'}
                        </div>
                      </div>
                      {hasActiveFilters && (
                        <Button size="sm" variant="outline" onClick={clearFilters} className="mt-2">
                          <X className="w-3 h-3 mr-1.5" /> Reset Filter
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {logs.map((log, idx) => {
                const meta = TEMPLATE_META[log.template_type] || { label: log.template_type, color: 'bg-slate-100 text-slate-700 border border-slate-200', icon: Mail };
                const Icon = meta.icon;
                const accent = ACCENT_BAR[log.template_type] || 'bg-slate-300';
                const t = formatTimeShort(log.sent_at || log.created_at);
                const zebra = idx % 2 === 1 ? 'bg-slate-50/40 dark:bg-slate-800/20' : '';
                return (
                  <tr
                    key={log.id}
                    className={`group border-b border-slate-100 dark:border-slate-800/50 ${zebra} hover:bg-blue-50/40 dark:hover:bg-slate-800/60 hover:shadow-[inset_2px_0_0_0_rgb(59_130_246)] transition-all duration-200 animate-in fade-in slide-in-from-bottom-2`}
                    style={{ animationDelay: `${Math.min(idx * 30, 500)}ms`, animationFillMode: 'backwards' }}
                    data-testid={`email-log-row-${log.id}`}
                  >
                    <td className="py-4 px-6 whitespace-nowrap relative">
                      {/* Accent bar per template */}
                      <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r ${accent} opacity-70 group-hover:opacity-100 group-hover:h-10 transition-all duration-200`} aria-hidden="true" />
                      <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 tabular-nums leading-tight">
                        {typeof t === 'object' ? t.time : '-'}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5 opacity-60" />
                        {typeof t === 'object' ? t.day : ''}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${accent} bg-opacity-15 text-slate-700 dark:text-slate-200 border border-white dark:border-slate-700 shadow-sm`}
                             style={{ backgroundColor: undefined }}
                             aria-hidden="true">
                          {(log.customer_name || '?').trim().charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{log.customer_name || '-'}</div>
                          {log.customer_account && (
                            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 tabular-nums">
                              #{log.customer_account}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <Badge className={`${meta.color} text-[11px] py-1 px-2.5 inline-flex items-center gap-1.5 font-medium`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </Badge>
                    </td>
                    <td className="py-4 px-6 text-sm text-slate-600 dark:text-slate-300">
                      <div className="max-w-[220px] truncate" title={log.recipient}>{log.recipient || '-'}</div>
                    </td>
                    <td className="py-4 px-6 whitespace-nowrap">
                      {log.invoice_number ? (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2.5 py-1 rounded-md">
                          <FileText className="w-3 h-3 opacity-60" />
                          {log.invoice_number}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4 whitespace-nowrap">
                      <div className="flex justify-center">
                        {log.status === 'sent' ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800 text-[11px] py-1 px-2.5 inline-flex items-center gap-1.5 font-medium">
                            <MailCheck className="w-3 h-3" /> Terkirim
                          </Badge>
                        ) : (
                          <Badge
                            className="bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800 text-[11px] py-1 px-2.5 inline-flex items-center gap-1.5 font-medium cursor-help"
                            title={log.error_message || 'Gagal terkirim'}
                          >
                            <MailX className="w-3 h-3" /> Gagal
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex justify-center">
                        {log.invoice_id && log.template_type === 'payment_confirmation' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResendPayment(log)}
                            title={`Kirim ulang email konfirmasi ke ${log.recipient}`}
                            data-testid={`email-log-resend-${log.id}`}
                            className="h-8 px-3 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 gap-1.5 rounded-lg"
                          >
                            <Send className="w-3.5 h-3.5" />
                            Kirim Ulang
                          </Button>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600 text-xs">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {total > pageSize && (
          <div className="border-t border-slate-200 dark:border-slate-700/60 px-6 py-4 bg-slate-50/30 dark:bg-slate-800/20">
            <Pagination
              currentPage={page}
              totalItems={total}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default EmailLogs;
